import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useTranslation } from 'react-i18next';

import TooltipIcon from '../../../../../../../../components/tooltip-icon/TooltipIcon';
import { useGetCertificatePermissionsById } from '../../../../../../../../hooks/api/certificates';
import { Locale } from '../../../../../../../../i18n';
import { parseDateWithoutTime } from '../../../../../../../../shared/helpers/parse-date';
import {
  CertificatePermissionType,
  ICertificatePermission,
} from '../../../../../../../../shared/interfaces/ICertificate';
import styles from './CertificatePermissionsTable.module.css';
import CertificatePermissionStatus from './CertificatePermissionStatus';
import WithdrawCertificateButton from './WithdrawCertificateButton';

type CertificatePermissionsTableProps = {
  certificateId?: string;
};

const CertificatePermissionsTable = ({
  certificateId,
}: CertificatePermissionsTableProps): JSX.Element => {
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const { data: permissions = [], isLoading } = useGetCertificatePermissionsById(
    { certificateId: certificateId ?? '' },
    {
      enabled: !!certificateId,
    },
  );

  const companyTemplate = (rowData: ICertificatePermission): React.ReactNode => {
    return (
      <div className='flex flex-column gap-1'>
        <span>{rowData.consumerCompanyName}</span>
        <span className='text-600 text-xs'>{rowData.consumerCompanyBpn}</span>
      </div>
    );
  };

  const typeTemplate = (rowData: ICertificatePermission): React.ReactNode => {
    if (rowData.type === CertificatePermissionType.PUSH) {
      return (
        <TooltipIcon
          id={`certificate-type-pushed-${rowData.id}`}
          tooltipText={t('certificateDetailsPage.tabs.permissionsTab.types.pushed')}
          icon={<i className='pi pi-send text-gray-700' />}
          width='fit'
        />
      );
    }

    return (
      <TooltipIcon
        id={`certificate-type-access-granted-${rowData.id}`}
        tooltipText={t('certificateDetailsPage.tabs.permissionsTab.types.accessGranted')}
        icon={<i className='pi pi-eye text-gray-700' />}
        width='fit'
      />
    );
  };

  const dateSharedTemplate = (rowData: ICertificatePermission): React.ReactNode => {
    return parseDateWithoutTime(rowData.dateCreated, language as Locale);
  };

  const sharedByTemplate = (rowData: ICertificatePermission): React.ReactNode => {
    const firstName = rowData.approvedByUser?.firstName || '';
    const lastName = rowData.approvedByUser?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || '-';
  };

  const lastRetrievedTemplate = (rowData: ICertificatePermission): React.ReactNode => {
    if (!rowData.lastRetrieved) {
      return (
        <span className='font-light font-italic text-gray-700'>
          {t('certificateDetailsPage.tabs.permissionsTab.notYet')}
        </span>
      );
    }

    return parseDateWithoutTime(rowData.lastRetrieved, language as Locale);
  };

  const statusTemplate = (rowData: ICertificatePermission): React.ReactNode => {
    if (!rowData.lastRetrieved && rowData.type === CertificatePermissionType.PULL) {
      // If it is a PULL entry and has not been retrieved yet, show the Withdraw button
      return <WithdrawCertificateButton permissionId={rowData.id} certificateId={certificateId} />;
    }

    return <CertificatePermissionStatus status={rowData.consumerReviewStatus} />;
  };

  return (
    <div className={styles['certificate-permissions-table']}>
      <DataTable
        value={permissions}
        loading={isLoading}
        scrollable
        tableStyle={{
          tableLayout: 'fixed',
          minWidth: '900px',
        }}
        emptyMessage={t('global.emptyTableMessage')}
        sortField='dateCreated'
        sortOrder={-1}
      >
        <Column
          header={t(
            'certificateDetailsPage.tabs.permissionsTab.permissionsTableColumns.companyName',
          )}
          body={companyTemplate}
          style={{ width: '28%', wordBreak: 'break-word' }}
        />

        <Column
          header={t('certificateDetailsPage.tabs.permissionsTab.permissionsTableColumns.type')}
          body={typeTemplate}
          style={{ width: '8%' }}
        />

        <Column
          header={t(
            'certificateDetailsPage.tabs.permissionsTab.permissionsTableColumns.dateShared',
          )}
          body={dateSharedTemplate}
          style={{ width: '16%' }}
        />

        <Column
          header={t('certificateDetailsPage.tabs.permissionsTab.permissionsTableColumns.sharedBy')}
          body={sharedByTemplate}
          style={{ width: '18%' }}
        />

        <Column
          header={t(
            'certificateDetailsPage.tabs.permissionsTab.permissionsTableColumns.lastRetrieved',
          )}
          body={lastRetrievedTemplate}
          style={{ width: '16%' }}
        />

        <Column
          header={t('certificateDetailsPage.tabs.permissionsTab.permissionsTableColumns.status')}
          body={statusTemplate}
          style={{ width: '14%' }}
        />
      </DataTable>
    </div>
  );
};

export default CertificatePermissionsTable;
